import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge, TableSkeleton, EmptyState } from "@/components/shared";
import SubmitCandidateDialog from "@/components/submissions/SubmitCandidateDialog";
import { SubmissionReviewDialog } from "@/components/submissions/SubmissionReviewDialog";
import { fmtDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { ClipboardCheck, Plus, Send } from "lucide-react";

export default function Submissions() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [reviewId, setReviewId] = useState(null);

  const load = useCallback(async () => {
    try {
      const [l, a] = await Promise.all([
        api.get("/submissions", { params: { page_size: 40 } }),
        api.get("/submissions/analytics"),
      ]);
      setList(l.data);
      setAnalytics(a.data);
    } catch (e) {
      toast.error(apiErr(e));
      setList({ items: [] });
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  return (
    <div>
      <PageHeader
        title="Submissions"
        subtitle="Formal candidate submissions and client analytics"
        actions={
          can("submission:create") && (
            <Button
              size="sm"
              onClick={() => setSubmitOpen(true)}
              data-testid="submissions-create-button"
              className="gap-1.5 bg-[#255058] hover:bg-[#1c3d43] text-white"
            >
              <Plus className="h-4 w-4" /> New Submission
            </Button>
          )
        }
      />
      <Tabs defaultValue="records">
        <TabsList>
          <TabsTrigger value="records" data-testid="submissions-tab-records">
            Records
          </TabsTrigger>
          <TabsTrigger value="analytics" data-testid="submissions-tab-analytics">
            Client Analytics
          </TabsTrigger>
        </TabsList>

        <TabsContent value="records" className="mt-4">
          <Card className="overflow-hidden">
            {!list ? (
              <div className="p-4">
                <TableSkeleton />
              </div>
            ) : list.items.length === 0 ? (
              <EmptyState
                icon={Send}
                title="No submissions yet"
                description="Submit a candidate to a job to get started."
              />
            ) : (
              <div className="overflow-x-auto scrollbar-thin">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Candidate</TableHead>
                      <TableHead>Job</TableHead>
                      <TableHead className="hidden md:table-cell">Client</TableHead>
                      <TableHead className="hidden lg:table-cell">Recruiter</TableHead>
                      <TableHead>Internal</TableHead>
                      <TableHead className="hidden md:table-cell">Client Process</TableHead>
                      <TableHead>Review</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {list.items.map((s) => (
                      <TableRow
                        key={s.id}
                        data-testid={`submission-row-${s.id}`}
                        className="hover:bg-accent/40"
                      >
                        <TableCell className="font-medium">{s.candidate_name}</TableCell>
                        <TableCell className="text-sm">{s.job_title}</TableCell>
                        <TableCell className="hidden md:table-cell text-sm text-muted-foreground">
                          {s.client_name}
                        </TableCell>
                        <TableCell className="hidden lg:table-cell text-sm text-muted-foreground">
                          {s.recruiter_name}
                        </TableCell>
                        <TableCell>
                          <StatusBadge value={s.internal_review_status || s.stage} />
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                          <StatusBadge value={s.client_process_status || s.stage} />
                        </TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setReviewId(s.id)}
                            data-testid={`submission-review-button-${s.id}`}
                          >
                            <ClipboardCheck className="mr-1.5 h-3.5 w-3.5" /> Review
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="analytics" className="mt-4 space-y-4">
          {!analytics ? (
            <TableSkeleton />
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {Object.entries(analytics.conversions).map(([k, v]) => (
                  <Card key={k} className="p-4">
                    <div className="text-xs text-muted-foreground">{k.replace(/_/g, " ")}</div>
                    <div className="font-display text-2xl font-bold mt-1">{v}%</div>
                  </Card>
                ))}
              </div>
              <Card className="overflow-hidden">
                <div className="px-4 py-3 border-b border-border font-display font-semibold">
                  By Client
                </div>
                <div className="overflow-x-auto scrollbar-thin">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Client</TableHead>
                        <TableHead>Submissions</TableHead>
                        <TableHead>Placements</TableHead>
                        <TableHead>Fill %</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {analytics.by_client.map((c) => (
                        <TableRow key={c.client_id} className="hover:bg-accent/40">
                          <TableCell className="font-medium">{c.client}</TableCell>
                          <TableCell>{c.submissions}</TableCell>
                          <TableCell>{c.placements}</TableCell>
                          <TableCell>
                            {c.submissions ? Math.round((c.placements / c.submissions) * 100) : 0}%
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </Card>
              <Card className="overflow-hidden">
                <div className="px-4 py-3 border-b border-border font-display font-semibold">
                  By Recruiter
                </div>
                <div className="overflow-x-auto scrollbar-thin">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Recruiter</TableHead>
                        <TableHead>Submissions</TableHead>
                        <TableHead>Placements</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {analytics.by_recruiter.map((r, i) => (
                        <TableRow key={i} className="hover:bg-accent/40">
                          <TableCell className="font-medium">{r.recruiter}</TableCell>
                          <TableCell>{r.submissions}</TableCell>
                          <TableCell>{r.placements}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </Card>
            </>
          )}
        </TabsContent>
      </Tabs>
      <SubmitCandidateDialog
        open={submitOpen}
        onClose={() => setSubmitOpen(false)}
        onDone={() => {
          setSubmitOpen(false);
          load();
        }}
      />
      <SubmissionReviewDialog
        submissionId={reviewId}
        open={Boolean(reviewId)}
        onClose={() => setReviewId(null)}
        onChanged={load}
      />
    </div>
  );
}
